import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProxySearchForm, ProxySearchResultsTable, ProxyEditPanel } from './components'
import { ProxySearchResult, ProxyGroup } from '../../../components/common/config/adminConfig'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import Loader from '../../../components/common/Loader'
import {
  selectRequestorProxyData,
  selectRequestorProxyLoading,
  selectRequestorProxyError,
  selectRequestorProxySearchPerformed,
  selectFetchCurrentLoading,
  selectCurrentProxies,
  selectFetchCurrentError,
  fetchCurrentRequestorProxies,
  clearRequestorProxyState,
  type RequestorProxyRecord,
  type CurrentProxyRecord,
} from '../../../store/slices/admin/requestorProxySlice'
import {
  selectApproverProxyData,
  selectApproverProxyLoading,
  selectApproverProxyError,
  selectApproverProxySearchPerformed,
  selectApproverFetchCurrentLoading,
  selectApproverCurrentProxies,
  selectApproverFetchCurrentError,
  fetchCurrentApproverProxies,
  clearApproverProxyState,
  type ApproverProxyRecord,
  type CurrentApproverProxyRecord,
} from '../../../store/slices/admin/approverProxySlice'
import {
  selectFloatEmployeeData,
  selectFloatEmployeeLoading,
  selectFloatEmployeeError,
  selectFloatEmployeeSearchPerformed,
  selectFloatFetchCurrentLoading,
  selectFloatCurrentEmployees,
  selectFloatFetchCurrentError,
  fetchCurrentFloatEmployees,
  clearFloatEmployeeState,
  type FloatEmployeeSearchRecord,
  type CurrentFloatEmployeeRecord,
} from '../../../store/slices/admin/floatEmployeeSlice'
import NotFoundPage from '../../NotFoundPage'

type ProxyType = 'approver' | 'requestor' | 'float'

interface ManageProxyProps {
  /** Override the proxy type instead of inferring it from the URL param. */
  initialType?: ProxyType
}

/**
 * Maps a requestor-proxy API record to the shared {@link ProxySearchResult}
 * shape consumed by the results table.
 *
 * @param record - Raw record from the requestor-proxy search API.
 * @returns The normalized table row model.
 */
const mapRequestorRecordToResult = (
  record: RequestorProxyRecord
): ProxySearchResult => ({
  id: record.approvalGrpId,
  name: record.approver,
  email: '',
  groupId: record.approvalGrpId,
  department: record.department,
  approvalGrpId: record.approvalGrpId,
  approver: record.approver,
  region: record.region,
  regionCode: record.regionCode,
  reqstorProxy: record.reqstorProxy,
})

/**
 * Maps an approver-proxy API record (searchAP) to the shared
 * {@link ProxySearchResult} shape. `groupId` is set so the results table
 * (which filters on it) renders the row; `approver` carries the primary
 * approver and `approverProxy` the "; "-joined proxy list. `regionCode` is
 * captured for use in assignment operations.
 *
 * @param record - Raw record from the approver-proxy search API.
 * @returns The normalized table row model.
 */
const mapApproverRecordToResult = (
  record: ApproverProxyRecord
): ProxySearchResult => ({
  id: record.approvalGrpId,
  name: record.primaryApprover,
  email: '',
  groupId: record.approvalGrpId,
  department: record.department,
  approvalGrpId: record.approvalGrpId,
  approver: record.primaryApprover,
  approverProxy: record.approverProxy,
  region: record.region,
  regionCode: record.regionCode,
})

/**
 * Maps a float-employee search record (searchFE) to the shared
 * {@link ProxySearchResult} shape consumed by the results table.
 * `regionCode` is captured for use in assignment operations.
 *
 * @param record - Raw record from the float-employee search API.
 * @returns The normalized table row model.
 */
const mapFloatRecordToResult = (
  record: FloatEmployeeSearchRecord
): ProxySearchResult => ({
  id: record.approvalGrpId,
  name: record.primaryApprover,
  email: '',
  groupId: record.approvalGrpId,
  department: record.department,
  approvalGrpId: record.approvalGrpId,
  approver: record.primaryApprover,
  floatEmployee: record.floatEmployee,
  region: record.region,
  regionCode: record.regionCode,
})

/**
 * Maps an approver current-proxy record (fetchCurrentAP) to the shared
 * {@link CurrentProxyRecord} shape consumed by the edit panel. The approver
 * API exposes no dedicated NUID, so `value` is left blank (the panel derives a
 * stable row key from the array index); nullable fields default to '' except
 * `endDate`, which preserves `null` to signal an open-ended assignment.
 *
 * @param record - Raw record from the fetchCurrentAP API.
 * @returns The normalized current-proxy model.
 */
const mapApproverCurrentToRecord = (
  record: CurrentApproverProxyRecord
): CurrentProxyRecord => ({
  label: record.name,
  value: '',
  region: record.region,
  department: record.department ?? '',
  email: record.email ?? '',
  startDate: record.from ?? '',
  endDate: record.to,
})

/**
 * Maps a float-employee current record (fetchCurrentFE) to the shared
 * {@link CurrentProxyRecord} shape consumed by the edit panel. The float API
 * returns dates in Oracle DD-MON-YY format which `toMMDDYYYY` in ProxyEditPanel
 * handles during rendering. Nullable fields default to '' except `endDate`.
 *
 * @param record - Raw record from the fetchCurrentFE API.
 * @returns The normalized current-proxy model.
 */
const mapFloatCurrentToRecord = (
  record: CurrentFloatEmployeeRecord
): CurrentProxyRecord => ({
  label: record.floatEmployee,
  value: '',
  region: record.region,
  department: record.department ?? '',
  email: record.email ?? '',
  startDate: record.startDate ?? '',
  endDate: record.endDate,
})

/**
 * Returns the approver, float, or requestor value based on the active proxy mode.
 * Extracted to avoid nested ternary code smells.
 */
function resolveByMode<T>(
  isApproverMode: boolean,
  isFloatMode: boolean,
  approverValue: T,
  floatValue: T,
  requestorValue: T
): T {
  if (isApproverMode) return approverValue
  if (isFloatMode) return floatValue
  return requestorValue
}

/**
 * Container page for managing proxy assignments.
 * Reads search results and loading state from the Redux store
 * (populated by the {@link searchRequestorProxy} thunk dispatched in ProxySearchForm).
 */
const ManageProxy: React.FC<ManageProxyProps> = ({ initialType }) => {
  const { type } = useParams()

  const mapSlugToType = (slug?: string | null): ProxyType | undefined => {
    if (!slug) return undefined
    const s = slug.toLowerCase()
    if (s === 'approver-proxy' ) return 'approver'
    if (s === 'requestor-proxy') return 'requestor'
    if (s === 'float-proxy') return 'float'
    return undefined
  }

  const proxyType = (mapSlugToType(type) as ProxyType) || (initialType as ProxyType)
  const isApproverMode = proxyType === 'approver'
  const isFloatMode = proxyType === 'float'

  // Edit-panel local state — independent of search results
  const [selected, setSelected] = useState<ProxySearchResult | null>(null)
  const [groups, setGroups] = useState<ProxyGroup[]>([])

  const dispatch = useAppDispatch()

  // Search state from Redux — each mode reads its dedicated slice.
  const requestorData = useAppSelector(selectRequestorProxyData)
  const requestorLoading = useAppSelector(selectRequestorProxyLoading)
  const requestorSearchPerformed = useAppSelector(selectRequestorProxySearchPerformed)
  const requestorError = useAppSelector(selectRequestorProxyError)

  const approverData = useAppSelector(selectApproverProxyData)
  const approverLoading = useAppSelector(selectApproverProxyLoading)
  const approverSearchPerformed = useAppSelector(selectApproverProxySearchPerformed)
  const approverError = useAppSelector(selectApproverProxyError)

  const floatData = useAppSelector(selectFloatEmployeeData)
  const floatLoading = useAppSelector(selectFloatEmployeeLoading)
  const floatSearchPerformed = useAppSelector(selectFloatEmployeeSearchPerformed)
  const floatError = useAppSelector(selectFloatEmployeeError)

  const isLoading = resolveByMode(isApproverMode, isFloatMode, approverLoading, floatLoading, requestorLoading)
  const searchPerformed = resolveByMode(isApproverMode, isFloatMode, approverSearchPerformed, floatSearchPerformed, requestorSearchPerformed)
  const searchError = resolveByMode<string | null>(isApproverMode, isFloatMode, approverError, floatError, requestorError)

  // Hide the edit panel whenever a new search is started
  useEffect(() => {
    if (isLoading) {
      setSelected(null)
    }
  }, [isLoading])

  // Clear all Redux state for the active mode when the user navigates away
  // so stale errors and results do not re-appear on re-entry.
  useEffect(() => {
    return () => {
      if (isApproverMode) dispatch(clearApproverProxyState())
      else if (isFloatMode) dispatch(clearFloatEmployeeState())
      else dispatch(clearRequestorProxyState())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isApproverMode, isFloatMode])

  // Fetch-current state from Redux — each mode reads its dedicated slice.
  const requestorFetchCurrentLoading = useAppSelector(selectFetchCurrentLoading)
  const requestorCurrentProxies = useAppSelector(selectCurrentProxies)
  const requestorFetchCurrentError = useAppSelector(selectFetchCurrentError)

  const approverFetchCurrentLoading = useAppSelector(selectApproverFetchCurrentLoading)
  const approverCurrentProxies = useAppSelector(selectApproverCurrentProxies)
  const approverFetchCurrentError = useAppSelector(selectApproverFetchCurrentError)

  const floatFetchCurrentLoading = useAppSelector(selectFloatFetchCurrentLoading)
  const floatCurrentEmployees = useAppSelector(selectFloatCurrentEmployees)
  const floatFetchCurrentError = useAppSelector(selectFloatFetchCurrentError)

  const isLoadingFetchCurrent = resolveByMode(
    isApproverMode,
    isFloatMode,
    approverFetchCurrentLoading,
    floatFetchCurrentLoading,
    requestorFetchCurrentLoading
  )

  const fetchCurrentError = resolveByMode<string | null>(
    isApproverMode,
    isFloatMode,
    approverFetchCurrentError,
    floatFetchCurrentError,
    requestorFetchCurrentError
  )

  // Normalize current records to the shared edit-panel shape for each mode.
  const currentProxies: CurrentProxyRecord[] = useMemo(() => {
    if (isApproverMode) return approverCurrentProxies.map(mapApproverCurrentToRecord)
    if (isFloatMode) return floatCurrentEmployees.map(mapFloatCurrentToRecord)
    return requestorCurrentProxies
  }, [isApproverMode, isFloatMode, approverCurrentProxies, floatCurrentEmployees, requestorCurrentProxies])

  // Only map results after a search has been performed and the call has settled.
  // Passing `undefined` hides the results table entirely (no "No Results Found" on initial load).
  const results: ProxySearchResult[] | undefined = useMemo(() => {
    if (!searchPerformed || isLoading) return undefined
    if (isApproverMode) return approverData.map(mapApproverRecordToResult)
    if (isFloatMode) return floatData.map(mapFloatRecordToResult)
    return requestorData.map(mapRequestorRecordToResult)
  }, [searchPerformed, isLoading, isApproverMode, isFloatMode, approverData, floatData, requestorData])

  const title = useMemo(() => {
    switch (proxyType) {
      case 'requestor':
        return 'Assign Requestor Proxy'
      case 'float':
        return 'Assign Float Employee'
      default:
        return 'Assign Approver Proxy'
    }
  }, [proxyType])

  if (!proxyType) {
    return <NotFoundPage />
  }

  const handleEdit = (r: ProxySearchResult) => {
    setSelected(r)
    setGroups([])
    // Fetch current assignments for the selected approval group using the
    // mode-appropriate endpoint.
    if (r.approvalGrpId) {
      if (isApproverMode) {
        dispatch(fetchCurrentApproverProxies(r.approvalGrpId))
      } else if (isFloatMode) {
        dispatch(fetchCurrentFloatEmployees(r.approvalGrpId))
      } else {
        dispatch(fetchCurrentRequestorProxies(r.approvalGrpId))
      }
    }
  }

  return (
    <div className="pt-6 pb-6 page-container">
      {/* Global loader covers the page during any API call */}
      {(isLoading || isLoadingFetchCurrent) && <Loader />}

      {/* ProxySearchForm dispatches the search thunk internally */}
      <ProxySearchForm title={title} mode={proxyType} />

      {searchError && (
        <p role="alert" className="p4 -medium mt-4" style={{ color: '#c0392b' }}>
          {searchError}
        </p>
      )}

      {fetchCurrentError && (
        <p role="alert" className="p4 -medium mt-4" style={{ color: '#c0392b' }}>
          {fetchCurrentError}
        </p>
      )}

      <ProxySearchResultsTable results={results} mode={proxyType} onEdit={handleEdit} />

      {selected && (
        <ProxyEditPanel
          row={selected}
          groups={groups}
          mode={proxyType}
          currentProxies={currentProxies}
          isLoadingFetchCurrent={isLoadingFetchCurrent}
        />
      )}
    </div>
  )
}

export default ManageProxy
